import { Filter, Pagination, Search, Sort } from "@/components/Table/Table";
import { YearStatus } from "@/components/YearSelector/YearSelector";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { filterSearchToGetFilter, GetData, sortToParam } from "@/utilities/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { PackagingMaterial } from "./PackagingMaterialOverview/PackagingMaterialsOverview";
import { IImpact } from "@/pages/dashboard/utils/types";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";

export interface GetPackagingMaterialsApi {
  total_count: number;
  complete_count?: number;
  incomplete_count?: number;
  out_of_scope_count?: number;
  filter_options?: Filter;
  materials: PackagingMaterial[];
  status: "success" | "failed";
}

export interface GetPackagingMaterials extends GetData<PackagingMaterial> {}

export function useGetPackagingMaterials({
  year,
  showUnrelated = false,
  pagination,
  filter,
  search,
  sort,
  enabled,
}: {
  year?: number;
  showUnrelated?: boolean;
  pagination?: Pagination;
  filter?: Filter;
  search?: Search;
  sort?: Sort;
  enabled?: boolean;
}) {
  const yearParam = year ? { year } : {};
  const showUnrelatedParam = showUnrelated ? { show_unrelated: showUnrelated } : {};

  return useQuery({
    queryKey: ["packagings", "materials", year, showUnrelated, pagination, filter, search, sort],
    keepPreviousData: true,
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<GetPackagingMaterialsApi>("/packaging/materials", {
          params: {
            ...yearParam,
            ...showUnrelatedParam,
            page: pagination?.page === undefined ? 1 : pagination.page,
            page_size: pagination?.pageSize || 50,
            ...filterSearchToGetFilter(filter, search),
            ...sortToParam(sort),
          },
        })
        .then(
          (result) => ({ ...result.data, data: result.data.materials }) as GetPackagingMaterials
        ),
  });
}

export interface PackagingMaterialDetail extends PackagingMaterial {
  match?: ImpactDataset;
  supplier_address_is_valid?: boolean;
}

export function useGetPackagingMaterial({
  materialId,
  enabled,
}: {
  materialId: number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["packagings", "material", materialId, "details"],
    enabled: enabled ?? true,
    queryFn: () =>
      axios
        .get<PackagingMaterialDetail>("/packaging/materials/" + materialId)
        .then((result) => result.data),
  });
}

export interface PackagingMaterialPatch {
  name?: string;
  impact_dataset_id?: number;
  clear?: "impact_dataset_id"[];
}

export function usePatchPackagingMaterial() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ materialId, patch }: { materialId: number; patch: PackagingMaterialPatch }) =>
      axios.patch("/packaging/materials/" + materialId, patch),
    onSuccess: (_, { materialId }) => {
      smartQueryClient.invalidateQueries(["dashboard"]);
    },
    onMutate: ({ materialId, patch }) => {
      smartQueryClient.setQueryData<PackagingMaterial>(
        ["packagings", "material", materialId, "details"],
        (data) => {
          if (!data) return undefined;
          return { ...data, ...patch };
        }
      );
    },
  });
}

export function useBulkPatchPackagingMaterial() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      materialIds,
      patch,
    }: {
      materialIds: number[];
      patch: PackagingMaterialPatch;
    }) => axios.patch("/packaging/materials?ids=" + materialIds.join(";"), patch),
    onSuccess: (_, { materialIds }) => {
      smartQueryClient.invalidateQueries(["packagings"]);
      smartQueryClient.invalidateQueries(["dashboard"]);
      materialIds.map((materialId) =>
        smartQueryClient.invalidateQueries(["packagings", "material", materialId])
      );
    },
  });
}

export interface PackagingRelatedProduct {
  id: number;
  external_id: string;
  name: string;
  relation: "primary" | "secondary";
  unrelated: boolean;
}

export interface GetPackagingMaterialRelatedProducts {
  related_packaging: PackagingRelatedProduct[];
  total_count: number;
}

export function useGetPackagingMaterialRelatedPackaging({
  materialId,
  pagination,
}: {
  materialId: number;
  pagination: Pagination;
}) {
  return useQuery({
    queryKey: ["packagings", "material", materialId, "related_packaging", pagination],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetPackagingMaterialRelatedProducts>(
          "/packaging/materials/" + materialId + "/related_packaging",
          {
            params: { page: pagination.page, page_size: pagination.pageSize },
          }
        )
        .then((result) => result.data),
  });
}

export function useDeletePackagingMaterials() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ materialIds }: { materialIds: number[] }) =>
      axios.delete("/packaging/materials", {
        params: {
          ids: materialIds.join(";"),
        },
      }),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["packagings"]);
    },
  });
}

export interface PackagingMaterialUnit {
  start_unit: string;
  target_unit: string;
  conversion_factor: number | null;
  status: "complete" | "incomplete" | "validating";
}

export type GetPackagingMaterialUnits = PackagingMaterialUnit[];

export function useGetPackagingMaterialUnits({ materialId }: { materialId: number }) {
  return useQuery({
    queryKey: ["packagings", "material", materialId, "units"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<GetPackagingMaterialUnits>("/packaging/materials/" + materialId + "/units")
        .then((result) => result.data),
  });
}

export interface UpdatePackagingMaterialUnit {
  start_unit: string;
  target_unit: string;
  factor: number | null;
}

export function useUpdatePackagingMaterialUnit() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({
      materialId,
      materialUnit,
    }: {
      materialId: number;
      materialUnit: UpdatePackagingMaterialUnit;
    }) =>
      axios.post<{ new_status: "complete" | "incomplete" }>(
        "/packaging/materials/" + materialId + "/units",
        materialUnit
      ),
    onSuccess: ({ data: { new_status: newStatus } }, { materialId, materialUnit }) => {
      smartQueryClient.invalidateQueries(["dashboard"]);
      smartQueryClient.invalidateQueries(["packagings", "material", materialId, "details"]);

      smartQueryClient.setQueryData<GetPackagingMaterialUnits>(
        ["packagings", "material", materialId, "units"],
        (materialUnits) => {
          return materialUnits
            ? materialUnits.map((unit) => {
                if (
                  unit.start_unit === materialUnit.start_unit &&
                  unit.target_unit === materialUnit.target_unit
                ) {
                  return {
                    ...unit,
                    status: newStatus,
                  };
                } else {
                  return unit;
                }
              })
            : undefined;
        }
      );
    },
  });
}

export function usePackagingMaterialStatusPerYear() {
  return useQuery({
    queryKey: ["packagings", "materials", "years"],
    keepPreviousData: true,
    queryFn: () =>
      axios.get<YearStatus[]>("/packaging/materials/years", {}).then((result) => result.data),
  });
}

export interface PackagingProductRelationsPerYear {
  year: number | null;
  packaging_count: number;
  facility_count: number;
}

export function usePackagingProductRelationsPerYear({ materialId }: { materialId: number }) {
  return useQuery({
    queryKey: ["packagings", "materials", materialId, "related_packaging_per_year"],
    keepPreviousData: true,
    queryFn: () =>
      axios
        .get<PackagingProductRelationsPerYear[]>(
          `/packaging/materials/${materialId}/related_packaging_per_year`
        )
        .then((result) => result.data),
  });
}

export interface PackagingMaterialMatchOption {
  value: string;
  label: string;
  dataset_id: number;
  unit: string;
  impact: IImpact;
  children?: PackagingMaterialMatchOption[];
}

export function useGetPackagingMaterialOptions() {
  return useQuery({
    queryKey: ["packagings", "materials", "matching", "options"],
    queryFn: () =>
      axios
        .get<PackagingMaterialMatchOption[]>(`/packaging/materials/match/options`)
        .then((result) => result.data),
  });
}
